import React from 'react';
import { ExternalLink, MapPin, Star, Users, Utensils } from 'lucide-react';

export const FavoritePlaces = () => {
  // This array can be easily modified to add/remove/update favorite places
  const favoritePlaces = [
    {
      id: 1,
      name: "Alexander Medical Center",
      description: "My professional home and collaboration partner. Excellent medical facility providing comprehensive healthcare services in Gilbert.",
      category: "Medical Center",
      location: "Gilbert, AZ",
      website: "https://www.alexandermedcenter.com/",
      icon: Users,
      logoUrl: "/api/uploads/alexander-medical-center.png",
      highlights: ["Professional Collaboration", "Comprehensive Care", "Modern Facilities"],
      personalNote: "This is where I provide physical therapy services in collaboration with an outstanding medical team."
    },
    {
      id: 2, 
      name: "Chandler Health and Performance",
      description: "Outstanding fitness and performance facility focusing on health, wellness, and athletic performance.",
      category: "Fitness & Performance",
      location: "Chandler, AZ", 
      website: "https://chandlerhealthperformance.com/",
      icon: Star,
      logoUrl: "/api/uploads/chandler-health-performance.png",
      highlights: ["Athletic Performance", "Health & Wellness", "Professional Training"],
      personalNote: "Great facility for clients looking to enhance their performance and maintain optimal health."
    },
    {
      id: 3,
      name: "Ginza Sushi Fusion Cuisine", 
      description: "Exceptional Japanese fusion restaurant offering fresh sushi and innovative dishes in a welcoming atmosphere.",
      category: "Restaurant",
      location: "Gold Canyon, AZ",
      website: "https://www.ginzafantasticfusion.com/",
      icon: Utensils,
      logoUrl: "https://customer-assets.emergentagent.com/job_physio-portal/artifacts/zkib2j5d_image_2025-09-30_162049903.png",
      highlights: ["Fresh Sushi", "Fusion Cuisine", "Great Atmosphere"],
      personalNote: "One of my favorite spots for excellent food and relaxation after a long day of helping patients."
    },
    {
      id: 4,
      name: "Happy's Hamburgers",
      description: "Classic hamburgers done right. A local favorite for fast, delicious burgers in Arizona.",
      category: "Restaurant",
      location: "Arizona",
      website: "https://happyshamburgers.com",
      icon: Utensils,
      logoUrl: "/api/uploads/happys-hamburgers.png",
      highlights: ["Classic Burgers", "Local Favorite", "Fast & Tasty"],
      personalNote: "A reliable go-to spot for a great burger after a long day."
    }
,
    {
      id: 5,
      name: "Trace Bundy",
      description: "World-class acoustic guitarist known for innovative fingerstyle techniques and captivating live performances.",
      category: "Musician",
      location: "Colorado",
      website: "https://tracebundy.com/",
      icon: Star,
      logoUrl: "/api/uploads/trace-bundy.jpg",
      highlights: ["Acoustic Fingerstyle", "Live Performances", "Instrumental Music"],
      personalNote: "A personal favorite for inspiring, technical, and soulful guitar music. Highly recommend catching a live show."
    }
  ];

  const handleVisitWebsite = (website, name) => {
    window.open(website, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen pt-8 pb-20 relative" style={{
      backgroundImage: 'url("https://images.unsplash.com/photo-1614019465575-16b8a61df7f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxTdXBlcnN0aXRpb24lMjBNb3VudGFpbnN8ZW58MHx8fHwxNzU4MjM0NDQ2fDA&ixlib=rb-4.1.0&q=85")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* Background overlay for better text readability */}
      <div className="absolute inset-0 bg-black/60 pointer-events-none"></div>
      
      {/* Page Header: add shared banner + pill reduced for continuity */}
      <div className="container mx-auto px-4 mb-6 relative z-10">
        <img
          src="/api/uploads/grr-banner-2025-new.jpg"
          alt="GymRatRehab Banner"
          className="w-full h-auto max-h-80 object-contain mx-auto"
          style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}
        />
        <div className="text-center mt-4">
          <span className="inline-block px-5 py-2 text-lg font-semibold uppercase tracking-wide rounded-full" style={{ backgroundColor: 'rgba(232, 184, 109, 0.2)', color: 'var(--brand-primary)' }}>
            Personal Recommendations
          </span>
        </div>
        <div className="text-center mt-8">
          <h1 className="heading-1 mb-6">
            My Favorite Places
          </h1>
          <p className="body-large max-w-3xl mx-auto">
            As an Arizona-born native, I've discovered some exceptional places throughout my home state 
            that I'm excited to share. These are businesses and locations I personally recommend and support.
          </p>
        </div>
      </div>

      {/* Favorite Places Grid */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-1 gap-12 max-w-4xl mx-auto">
          {favoritePlaces.map((place) => {
            const IconComponent = place.icon;
            return (
              <div key={place.id} className="bg-gray-900/50 rounded-2xl border border-gray-700 overflow-hidden hover:border-opacity-75 transition-all group relative">
                {place.logoUrl && (
                  <div
                    className="absolute inset-0 opacity-10 bg-center bg-no-repeat pointer-events-none"
                    style={{ 
                      backgroundImage: `url(${place.logoUrl})`,
                      backgroundSize: '180px auto',
                      filter: 'grayscale(100%)'
                    }}
                  ></div>
                )}
                <div className="p-8 relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className={`${place.id === 3 ? 'w-64 h-64' : 'w-32 h-32'} rounded-xl flex items-center justify-center relative overflow-hidden`} style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                        {place.logoUrl ? (
                          <img src={place.logoUrl} alt={`${place.name} logo`} className="absolute inset-0 w-full h-full object-contain p-3 opacity-90" />
                        ) : (
                          <IconComponent style={{ color: 'var(--brand-primary)' }} size={place.id === 3 ? 112 : 56} />
                        )}
                      </div>
                      <div>
                        <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-full" style={{ backgroundColor: 'rgba(232, 184, 109, 0.2)', color: 'var(--brand-primary)' }}>
                          {place.category}
                        </span>
                        <h2 className="heading-2 mt-2 mb-1" style={{ color: 'var(--brand-primary)' }}>
                          {place.name}
                        </h2>
                        <div className="flex items-center text-gray-400 text-sm">
                          <MapPin size={14} className="mr-1" />
                          {place.location}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleVisitWebsite(place.website, place.name)}
                      className="btn-primary flex items-center gap-2 hover:scale-105 transition-transform"
                    >
                      Visit Website
                      <ExternalLink size={16} />
                    </button>
                  </div>

                  <p className="body-medium mb-6">
                    {place.description}
                  </p>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {place.highlights.map((highlight, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-800 text-gray-300 text-sm rounded-full">
                        {highlight}
                      </span>
                    ))}
                  </div>

                  {/* Personal Note */}
                  <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(to right, rgba(232, 184, 109, 0.1), rgba(232, 184, 109, 0.05))', border: '1px solid rgba(232, 184, 109, 0.2)' }}>
                    <p className="text-sm italic" style={{ color: 'var(--text-secondary)' }}>
                      <strong style={{ color: 'var(--brand-primary)' }}>Douglas Mitchell, DPT's Note:</strong> {place.personalNote}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 p-8 rounded-2xl max-w-2xl mx-auto" style={{ background: 'linear-gradient(to right, rgba(232, 184, 109, 0.1), rgba(232, 184, 109, 0.05))', border: '1px solid rgba(232, 184, 109, 0.2)' }}>
          <h3 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>Have Questions About Physical Therapy?</h3>
          <p className="body-medium mb-6">
            While you're exploring these great local businesses, don't forget that I'm here to help you 
            maximize your body's healing potential through expert physical therapy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact?focus=contact" className="btn-primary">
              Schedule Consultation
            </a>
            <button 
              onClick={() => window.location.href = '/services'}
              className="btn-secondary"
            >
              View Services
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};